import { isExternalUrl } from '@shared/fondof-fn/url/isExternalUrl'
import {
  addTrailingSlash,
  ensureLeadingSlash,
  trimTrailingSlash,
} from '@shared/fondof-fn/url/slashes'
import { useLocationStore } from '@shared/global-store/stores/location/location'

const LEADING_LOCALE_SEGMENT_PATTERN = /^[a-z]{2}(?:-[a-z]{2})?$/i

type LocalizeUrlOptions = {
  replaceLeadingLocale?: boolean
  preserveQueryAndHash?: boolean
  trimTrailingSlash?: boolean
}

export const localizeUrl = (
  url: string,
  searchParams?: Record<string, string | number | boolean | undefined>,
  options?: LocalizeUrlOptions
) => {
  if (isExternalUrl(url)) {
    return url
  }

  const { locale, market, urlPrefix } = useLocationStore().urlPrefix
  const localePrefix = `/${urlPrefix ?? `${locale}-${market}`}`

  const { pathPart, queryString, hashPart, suffixPart } =
    splitPathAndSuffix(url)
  const localizedPath = localizePath(pathPart, localePrefix, options)
  const resultParams = mergeQueryParams(queryString, searchParams)
  const finalPath = finalizePath(localizedPath, options)

  if (options?.preserveQueryAndHash) {
    return `${finalPath}${suffixPart}`
  }

  const resultQueryString = resultParams.toString()
  if (!resultQueryString) {
    return `${finalPath}${hashPart}`
  }

  return `${finalPath}?${resultQueryString}${hashPart}`
}

const localizePath = (
  pathPart: string,
  localePrefix: string,
  options?: LocalizeUrlOptions
): string => {
  const normalizedPathPart = ensureLeadingSlash(pathPart)
  const pathForLocalization = options?.replaceLeadingLocale
    ? stripLeadingLocaleSegment(normalizedPathPart)
    : normalizedPathPart

  if (pathForLocalization.startsWith(localePrefix)) {
    return pathForLocalization
  }

  return `${localePrefix}${pathForLocalization}`
}

const mergeQueryParams = (
  queryString: string,
  searchParams?: Record<string, string | number | boolean | undefined>
): URLSearchParams => {
  const existingURLParams = queryString
    ? new URLSearchParams(queryString)
    : new URLSearchParams()
  const resultParams = new URLSearchParams()
  const overrideKeys = new Set(Object.keys(searchParams ?? {}))

  for (const [key, value] of existingURLParams.entries()) {
    if (!overrideKeys.has(key)) {
      resultParams.append(key, value)
    }
  }

  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== null && typeof value !== 'undefined') {
        resultParams.append(key, String(value))
      }
    }
  }

  return resultParams
}

const finalizePath = (localizedPath: string, options?: LocalizeUrlOptions) => {
  const pathWithTrailingSlash = hasFileExtension(localizedPath)
    ? localizedPath
    : addTrailingSlash(localizedPath)

  return options?.trimTrailingSlash
    ? trimTrailingSlash(pathWithTrailingSlash)
    : pathWithTrailingSlash
}

const splitPathAndSuffix = (
  input: string
): {
  pathPart: string
  queryString: string
  hashPart: string
  suffixPart: string
} => {
  const match = input.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/)

  if (!match) {
    return {
      pathPart: input,
      queryString: '',
      hashPart: '',
      suffixPart: '',
    }
  }

  const pathPart = match[1] ?? ''
  const queryPart = match[2] ?? ''
  const hashPart = match[3] ?? ''

  return {
    pathPart,
    queryString: queryPart.startsWith('?') ? queryPart.slice(1) : queryPart,
    hashPart,
    suffixPart: `${queryPart}${hashPart}`,
  }
}

const stripLeadingLocaleSegment = (path: string): string => {
  const hasLeadingSlash = path.startsWith('/')
  const segments = path.split('/').filter(Boolean)

  if (segments.length === 0) {
    return hasLeadingSlash ? '/' : ''
  }

  if (LEADING_LOCALE_SEGMENT_PATTERN.test(segments[0])) {
    segments.shift()
  }

  if (segments.length === 0) {
    return hasLeadingSlash ? '/' : ''
  }

  return `${hasLeadingSlash ? '/' : ''}${segments.join('/')}`
}

const hasFileExtension = (url: string) => {
  const lastSegment = url.split('/').pop() ?? ''

  // Find the last dot in the last path segment
  const lastDotIndex = lastSegment.lastIndexOf('.')
  if (lastDotIndex <= 0 || lastDotIndex === lastSegment.length - 1) {
    // No dot, dot is the first character, or dot is the last character:
    // not a valid file extension.
    return false
  }

  const extension = lastSegment.slice(lastDotIndex + 1).toLowerCase()

  // Common web file extensions; adjust as needed if the application serves others.
  const COMMON_FILE_EXTENSIONS = new Set([
    'html',
    'htm',
    'js',
    'mjs',
    'css',
    'json',
    'xml',
    'txt',
    'csv',
    'pdf',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'svg',
    'webp',
    'ico',
  ])

  return COMMON_FILE_EXTENSIONS.has(extension)
}
